/**
 * SiteRosterPage - Roster view for a specific site
 * Shows weekly calendar grid with full roster functionality
 * 
 * P0/P1 WFM Upgrade:
 * - Fixed timezone handling (UTC storage, Sydney display)
 * - Added Security Format View toggle
 * - Enhanced Copy Week with target date selection
 * - Added Clone Day feature
 * - Proper hour calculations
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { TimeInput24 } from '../components/TimeInput24';
import { 
  Calendar, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  User,
  Copy,
  Lock,
  Check,
  X,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Trash2,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  Timer,
  CheckCircle2,
  XCircle,
  Hourglass,
  LayoutGrid,
  Table
} from 'lucide-react';
import RosterSecurityFormatView from '../components/RosterSecurityFormatView';
import RosterTemplateLibrary from '../components/RosterTemplateLibrary';
import SaveAsTemplateDialog from '../components/SaveAsTemplateDialog';
import TimeInput24h from '../components/TimeInput24h';
import { parseTimeInput } from '../lib/timeparse';
import { 
  formatTimeLocal, 
  formatDateTimeLocal, 
  calculateHours, 
  formatHoursDecimal,
  localInputsToUTC,
  toDateInputValue
} from '../lib/timecalc';
import { formatDateKey } from '../lib/timeformat';

const ACCEPTANCE_COLORS = {
  scheduled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  pending_acceptance: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  accepted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  declined: 'bg-red-500/20 text-red-400 border-red-500/50',
  unaccepted: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  completed: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
};

const SHIFT_CARD_COLORS = {
  accepted: 'border-l-emerald-500 bg-emerald-500/5',
  pending_acceptance: 'border-l-blue-500 bg-blue-500/5',
  declined: 'border-l-red-500 bg-red-500/5',
  unaccepted: 'border-l-orange-500 bg-orange-500/5',
  scheduled: 'border-l-zinc-500 bg-zinc-500/5',
};

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Helper to get date string in YYYY-MM-DD format using LOCAL time (not UTC)
// This prevents timezone shifting when the date is used as a key
function toLocalDateKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatWeekRange(monday) {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${monday.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export default function SiteRosterPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, user, isAdmin, isOfficer } = useAuth();
  
  // ============================================================================
  // P0 ARCHITECTURE FIX: URL weekStart is the ONLY source of truth
  // Always compute "today" fresh to avoid stale closures
  // ============================================================================
  
  // Helper: Get Monday of current week - ALWAYS compute fresh
  const getTodayMonday = () => {
    const now = new Date();
    const monday = getMonday(now);
    console.log('[Roster] Fresh today Monday:', toLocalDateKey(monday), 'from', now.toISOString());
    return monday;
  };

  // Helper: Validate and parse weekStart from URL
  const parseWeekFromUrl = () => {
    const weekParam = searchParams.get('weekStart');
    console.log('[Roster] URL weekStart param:', weekParam);
    
    if (!weekParam) {
      console.log('[Roster] No weekStart in URL, will redirect to today');
      return null;
    }
    
    const parsedDate = new Date(weekParam);
    if (isNaN(parsedDate.getTime())) {
      console.warn('[Roster] Invalid date format in URL:', weekParam);
      return null;
    }
    
    const year = parsedDate.getFullYear();
    const now = new Date();
    
    // Validate: year must be reasonable
    if (year < 2020 || year > 2100) {
      console.warn('[Roster] Year out of range:', year);
      return null;
    }
    
    // P0 FIX: Tighter validation - within 8 weeks past, 52 weeks future
    const maxPast = new Date(now);
    maxPast.setDate(maxPast.getDate() - 56); // 8 weeks back
    const maxFuture = new Date(now);
    maxFuture.setDate(maxFuture.getDate() + 365); // ~1 year forward
    
    if (parsedDate < maxPast) {
      console.warn('[Roster] Date too far in past:', weekParam, '(max past:', maxPast.toISOString().split('T')[0], ')');
      return null;
    }
    
    if (parsedDate > maxFuture) {
      console.warn('[Roster] Date too far in future:', weekParam);
      return null;
    }
    
    const monday = getMonday(parsedDate);
    console.log('[Roster] Valid URL week, computed Monday:', monday.toISOString().split('T')[0]);
    return monday;
  };

  // P0 FIX: Initialize with fresh today if URL is missing/invalid
  const [currentWeek, setCurrentWeekInternal] = useState(() => {
    const weekParam = searchParams.get('weekStart');
    
    // No URL param - use today
    if (!weekParam) {
      const today = getTodayMonday();
      console.log('[Roster] No URL param, initializing with today:', today.toISOString().split('T')[0]);
      return today;
    }
    
    // Try to parse URL param
    const parsedDate = new Date(weekParam);
    if (isNaN(parsedDate.getTime())) {
      const today = getTodayMonday();
      console.log('[Roster] Invalid URL date, initializing with today:', today.toISOString().split('T')[0]);
      return today;
    }
    
    // Validate the date isn't too old
    const now = new Date();
    const maxPast = new Date(now);
    maxPast.setDate(maxPast.getDate() - 56); // 8 weeks
    
    if (parsedDate < maxPast || parsedDate.getFullYear() < 2020) {
      const today = getTodayMonday();
      console.warn('[Roster] URL date too old:', weekParam, '- resetting to today:', today.toISOString().split('T')[0]);
      return today;
    }
    
    return getMonday(parsedDate);
  });

  // P0 FIX: Single source of truth - URL drives state
  // This effect handles:
  // 1. Initial load with missing/invalid URL -> redirect to today
  // 2. Browser back/forward navigation -> sync state from URL
  // 3. Direct URL navigation -> sync state from URL
  useEffect(() => {
    const weekParam = searchParams.get('weekStart');
    
    // Compute the correct week we should be showing
    let targetWeek;
    let needsRedirect = false;
    
    // No week param in URL - redirect to current week
    if (!weekParam) {
      targetWeek = getTodayMonday();
      needsRedirect = true;
      console.log('[Roster] No weekStart param, will redirect to:', targetWeek.toISOString().split('T')[0]);
    } else {
      // Parse and validate URL week
      const parsedDate = new Date(weekParam);
      
      if (isNaN(parsedDate.getTime())) {
        targetWeek = getTodayMonday();
        needsRedirect = true;
        console.warn('[Roster] Invalid URL date format:', weekParam);
      } else {
        // Validate not too far in past (8 weeks max) or year too old
        const now = new Date();
        const maxPast = new Date(now);
        maxPast.setDate(maxPast.getDate() - 56);
        
        if (parsedDate < maxPast || parsedDate.getFullYear() < 2020) {
          targetWeek = getTodayMonday();
          needsRedirect = true;
          console.warn('[Roster] URL date too old:', weekParam);
        } else {
          // URL is valid
          targetWeek = getMonday(parsedDate);
          needsRedirect = false;
        }
      }
    }
    
    const targetWeekStr = toLocalDateKey(targetWeek);
    
    // Update URL if needed (only once, avoiding loops by checking URL value)
    if (needsRedirect && weekParam !== targetWeekStr) {
      console.log('[Roster] Redirecting URL to:', targetWeekStr);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('weekStart', targetWeekStr);
        return params;
      }, { replace: true });
    }
    
    // Always sync state to match target week
    setCurrentWeekInternal(prev => {
      const prevStr = toLocalDateKey(prev);
      if (prevStr !== targetWeekStr) {
        console.log('[Roster] Syncing state:', prevStr, '->', targetWeekStr);
        return targetWeek;
      }
      return prev;
    });
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams - NOT currentWeek to avoid loops

  // P0 FIX: Week navigation functions - update URL first, state will sync from effect
  const navigateToPrevWeek = useCallback(() => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    // P0 FIX: Use LOCAL date, not UTC (toISOString converts to UTC which causes timezone bugs)
    const year = prev.getFullYear();
    const month = String(prev.getMonth() + 1).padStart(2, '0');
    const day = String(prev.getDate()).padStart(2, '0');
    const weekStr = `${year}-${month}-${day}`;
    console.log('[Roster] Navigate to prev week:', weekStr);
    // Update both URL and state atomically to avoid race
    setCurrentWeekInternal(prev);
    setSearchParams(params => {
      const newParams = new URLSearchParams(params);
      newParams.set('weekStart', weekStr);
      return newParams;
    }, { replace: true });
  }, [currentWeek, setSearchParams]);

  const navigateToNextWeek = useCallback(() => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    // P0 FIX: Use LOCAL date, not UTC (toISOString converts to UTC which causes timezone bugs)
    const year = next.getFullYear();
    const month = String(next.getMonth() + 1).padStart(2, '0');
    const day = String(next.getDate()).padStart(2, '0');
    const weekStr = `${year}-${month}-${day}`;
    console.log('[Roster] Navigate to next week:', weekStr);
    // Update both URL and state atomically to avoid race
    setCurrentWeekInternal(next);
    setSearchParams(params => {
      const newParams = new URLSearchParams(params);
      newParams.set('weekStart', weekStr);
      return newParams;
    }, { replace: true });
  }, [currentWeek, setSearchParams]);

  // P0 FIX: Today button - ALWAYS compute fresh date, never use cached value
  const navigateToToday = useCallback(() => {
    // Compute fresh - do NOT use any cached/memoized value
    const now = new Date();
    const todayMonday = getMonday(now);
    // P0 FIX: Use LOCAL date, not UTC
    const year = todayMonday.getFullYear();
    const month = String(todayMonday.getMonth() + 1).padStart(2, '0');
    const day = String(todayMonday.getDate()).padStart(2, '0');
    const weekStr = `${year}-${month}-${day}`;
    console.log('[Roster] TODAY button clicked - fresh date:', now.toISOString(), '-> Monday:', weekStr);
    
    // Update both URL and state atomically
    setCurrentWeekInternal(todayMonday);
    setSearchParams(params => {
      const newParams = new URLSearchParams(params);
      newParams.set('weekStart', weekStr);
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // P0 FIX: Site change - preserve current week in URL
  const handleSiteChange = useCallback((newSiteId) => {
    // P0 FIX: Use LOCAL date, not UTC
    const year = currentWeek.getFullYear();
    const month = String(currentWeek.getMonth() + 1).padStart(2, '0');
    const day = String(currentWeek.getDate()).padStart(2, '0');
    const weekStr = `${year}-${month}-${day}`;
    console.log('[Roster] Switching site to:', newSiteId, 'week:', weekStr);
    navigate(`/roster/site/${newSiteId}?weekStart=${weekStr}`);
  }, [currentWeek, navigate]);
  
  // ============================================================================
  // End of P0 Week State Architecture
  // ============================================================================
  
  const [rosterData, setRosterData] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [sites, setSites] = useState([]);  // For quick switch
  const [loading, setLoading] = useState(true);
  const [officers, setOfficers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [copyKeepOfficers, setCopyKeepOfficers] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  
  // P1: View mode toggle (Standard vs Security Format)
  const [viewMode, setViewMode] = useState('standard'); // 'standard' or 'security'
  
  // P0-3: Copy Week target date
  const [copyTargetDate, setCopyTargetDate] = useState('');
  
  // P1: Edit Shift Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editFormData, setEditFormData] = useState({
    start_time: '',
    end_time: '',
    notes: '',
    break_minutes: 0,
  });
  
  // P1: Drag-to-Duplicate state
  const [draggedShift, setDraggedShift] = useState(null);
  const [isDragDuplicate, setIsDragDuplicate] = useState(false);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [altKeyPressed, setAltKeyPressed] = useState(false);

  const [formData, setFormData] = useState({
    officer_id: '',
    start_date: '',
    start_time: '06:00',
    end_time: '18:00',
    notes: '',
    break_minutes: 0,
    amendment_reason: '',  // P0: Amendment reason for committed weeks
    patrol_route_ids: [],  // P0: Patrol Route Assignment
    patrol_requirements: {},  // P0: {route_id: required_runs_count}
  });
  
  // P0: Patrol Routes for site
  const [availablePatrolRoutes, setAvailablePatrolRoutes] = useState([]);
  const [loadingPatrolRoutes, setLoadingPatrolRoutes] = useState(false);
  
  // P0: Amendment mode state
  const [showAmendmentPrompt, setShowAmendmentPrompt] = useState(false);
  const [pendingShiftData, setPendingShiftData] = useState(null);
  
  // P0: Bulk Delete state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // P1: Template-Based Smart Rostering state
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);

  // Track ALT key for drag-to-duplicate hint
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Alt') setAltKeyPressed(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Alt') setAltKeyPressed(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // P0 FIX: AbortController ref for cancelling in-flight requests
  const abortControllerRef = React.useRef(null);
  
  // P0 FIX: Track requested weekStart to validate responses
  const requestedWeekRef = React.useRef(null);

  // Fetch roster data
  const [rosterError, setRosterError] = useState(null);  // P0: Track error state
  
  // P0 FIX: Fetch with request/response correlation and abort support
  const fetchRoster = useCallback(async (weekOverride = null) => {
    // Use explicit week parameter or current state
    const weekToFetch = weekOverride || currentWeek;
    // P0 FIX: Use LOCAL date, not UTC (toISOString converts to UTC which causes timezone bugs)
    const year = weekToFetch.getFullYear();
    const month = String(weekToFetch.getMonth() + 1).padStart(2, '0');
    const day = String(weekToFetch.getDate()).padStart(2, '0');
    const weekStr = `${year}-${month}-${day}`;
    
    // P0 FIX: Abort any previous in-flight request
    if (abortControllerRef.current) {
      console.log('[RosterFetch] Aborting previous request');
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    requestedWeekRef.current = weekStr;
    
    console.log(`[RosterFetch] requestedWeekStart=${weekStr}`);
    
    try {
      setLoading(true);
      setRosterError(null);
      // P0 FIX: Clear rosterData immediately to prevent stale grid
      setRosterData(null);
      
      let url = `/roster/v2/site/${siteId}?week_start=${weekStr}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status_filter=${statusFilter}`;
      }
      
      const response = await api.get(url, { signal: controller.signal });
      
      // P0 FIX: Validate response matches requested week
      const responseWeekStart = response.data?.week_start;
      console.log(`[RosterFetch] responseWeekStart=${responseWeekStart}`);
      
      // Check if this response is for the current requested week
      if (requestedWeekRef.current !== weekStr) {
        console.log(`[RosterFetch] DISCARDED - stale response (requested=${requestedWeekRef.current}, got=${weekStr})`);
        return;
      }
      
      // DIAGNOSTIC: Log full response data
      console.log(`[RosterFetch] applied=true, ${Object.keys(response.data?.days || {}).length} days`);
      console.log('[RosterFetch] Response days detail:');
      Object.entries(response.data?.days || {}).forEach(([k, d]) => {
        console.log(`  ${k}: ${d.shifts?.length || 0} shifts, unfilled=${d.unfilled_count}`);
      });
      
      setRosterData(response.data);
      setSiteInfo(response.data.site);
    } catch (error) {
      // Ignore abort errors - they're expected when navigating quickly
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        console.log('[RosterFetch] Request aborted (expected)');
        return;
      }
      console.error('[RosterFetch] Error:', error);
      setRosterData(null);
      setRosterError(error.response?.data?.detail || error.message || 'Failed to load roster');
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [api, siteId, currentWeek, statusFilter]);

  const fetchOfficers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/users?roles=security_officer,supervisor&is_active=true');
      setOfficers(response.data || []);
    } catch (error) {
      console.error('Error fetching officers:', error);
    }
  }, [api, isAdmin]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, [api]);

  // P0: Fetch patrol routes for the current site
  const fetchPatrolRoutes = useCallback(async () => {
    if (!siteId) return;
    
    setLoadingPatrolRoutes(true);
    try {
      const response = await api.get(`/roster/patrol-routes/by-site/${siteId}`);
      setAvailablePatrolRoutes(response.data?.routes || []);
    } catch (error) {
      console.error('Error fetching patrol routes:', error);
      setAvailablePatrolRoutes([]);
    } finally {
      setLoadingPatrolRoutes(false);
    }
  }, [api, siteId]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // P0 FIX: Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetchOfficers();
    fetchSites();
  }, [fetchOfficers, fetchSites]);

  // P0: Fetch patrol routes when site changes
  useEffect(() => {
    fetchPatrolRoutes();
  }, [fetchPatrolRoutes]);

  // Navigation - using new P0 architecture functions defined above
  const handlePreviousWeek = navigateToPrevWeek;
  const handleNextWeek = navigateToNextWeek;
  const handleToday = navigateToToday;

  // Shift actions
  const handleCreateShift = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.start_date) {
      toast.error('Please select a date');
      return;
    }
    
    // Parse time inputs (handles 1800 → 18:00 conversion)
    const startTime = parseTimeInput(formData.start_time);
    const endTime = parseTimeInput(formData.end_time);
    
    if (!startTime || !endTime) {
      toast.error('Invalid time format. Use 24h format (e.g., 18:00 or 1800)');
      return;
    }
    
    try {
      // P0-1 FIX: Convert local Sydney time inputs to UTC for storage
      const startTimeUTC = localInputsToUTC(formData.start_date, startTime);
      let endTimeUTC = localInputsToUTC(formData.start_date, endTime);
      
      // Handle overnight shifts (end time is next day)
      if (endTime <= startTime) {
        // Add one day to end date using LOCAL date
        const [year, month, day] = formData.start_date.split('-').map(Number);
        const nextDay = new Date(year, month - 1, day + 1);
        const nextDayStr = toLocalDateKey(nextDay);
        endTimeUTC = localInputsToUTC(nextDayStr, endTime);
      }
      
      const payload = {
        site_id: siteId,
        officer_id: formData.officer_id || null,
        start_time: startTimeUTC,
        end_time: endTimeUTC,
        notes: formData.notes || null,
        require_acceptance: true,
        break_minutes: formData.break_minutes || 0,
        // P0: Patrol Route Assignment
        patrol_route_ids: formData.patrol_route_ids || [],
        patrol_requirements: formData.patrol_requirements || {},
      };
      
      // Debug log for development
      console.log('[CreateShift] Payload (UTC times):', JSON.stringify(payload, null, 2));
      
      const response = await api.post('/roster/shifts', payload);
      
      // Debug log response
      console.log('[CreateShift] Response:', JSON.stringify(response.data, null, 2));
      
      // P0 INTEGRITY: Do NOT show success unless response contains shift.id
      const createdShift = response.data;
      if (!createdShift?.id) {
        console.error('[CreateShift] ERROR: Response missing shift.id', createdShift);
        toast.error('Shift creation failed - invalid server response');
        return;
      }
      
      // P0 INTEGRITY: Immediately insert shift into local roster state
      // This prevents "false success" where toast shows but grid doesn't update
      // P0 FIX: Wrap in try-catch to prevent state update errors from triggering error boundary
      try {
        setRosterData(prevData => {
          if (!prevData?.days) {
            console.log('[CreateShift] ERROR: No prevData.days - cannot update local state');
            return prevData;
          }
          
          const shiftDate = createdShift.date;
          console.log('[CreateShift] DIAGNOSTIC:');
          console.log('  - shift.date:', shiftDate);
          console.log('  - available days in roster:', Object.keys(prevData.days));
          
          if (!shiftDate || !prevData.days[shiftDate]) {
            // Date not in current week view - just refetch
            console.log('[CreateShift] Shift date not in current week, will refetch');
            console.log('  - shiftDate:', shiftDate);
            console.log('  - prevData.days keys:', Object.keys(prevData.days));
            return prevData;
          }
          
          // Clone and update the specific day's shifts
          const updatedDays = { ...prevData.days };
          const dayData = { ...updatedDays[shiftDate] };
          dayData.shifts = [...(dayData.shifts || []), createdShift];
          dayData.shift_count = (dayData.shift_count || 0) + 1;
          
          // Update counts based on shift status
          if (!createdShift.officer_id) {
            dayData.unfilled_count = (dayData.unfilled_count || 0) + 1;
          } else {
            dayData.filled_count = (dayData.filled_count || 0) + 1;
            if (createdShift.acceptance_status === 'pending_acceptance') {
              dayData.pending_count = (dayData.pending_count || 0) + 1;
            }
          }
          
          // Update hours
          const shiftHours = createdShift.net_hours || createdShift.gross_hours || 0;
          dayData.total_hours = (dayData.total_hours || 0) + shiftHours;
          
          updatedDays[shiftDate] = dayData;
          
          // P0 INTEGRITY: Recalculate summary from same in-memory state
          const allShifts = Object.values(updatedDays).flatMap(d => d.shifts || []);
          const newSummary = {
            total_shifts: allShifts.length,
            filled: allShifts.filter(s => s.officer_id).length,
            unfilled: allShifts.filter(s => !s.officer_id).length,
            accepted: allShifts.filter(s => s.acceptance_status === 'accepted').length,
            pending: allShifts.filter(s => s.acceptance_status === 'pending_acceptance').length,
            declined: allShifts.filter(s => s.acceptance_status === 'declined').length,
            unaccepted: allShifts.filter(s => s.acceptance_status === 'unaccepted').length,
          };
          
          const newTotalHours = Object.values(updatedDays).reduce((sum, d) => sum + (d.total_hours || 0), 0);
          
          console.log('[CreateShift] Updated local state with new shift:', createdShift.id);
          
          return {
            ...prevData,
            days: updatedDays,
            summary: newSummary,
            total_hours: newTotalHours,
          };
        });
      } catch (stateError) {
        console.warn('[CreateShift] State update failed (non-fatal):', stateError);
        // Shift was created - state update failure is non-fatal
      }
      
      toast.success('Shift created successfully');
      setShowCreateDialog(false);
      setFormData({
        officer_id: '',
        start_date: '',
        start_time: '06:00',
        end_time: '18:00',
        notes: '',
        break_minutes: 0,
        patrol_route_ids: [],
        patrol_requirements: {},
      });
      
      // P0 FIX: Wrap fetchRoster in try-catch to prevent post-success errors
      // If refresh fails, the shift was still created - don't show fatal error
      try {
        await fetchRoster();
      } catch (refreshError) {
        console.warn('[CreateShift] Roster refresh failed after successful creation:', refreshError);
        // Non-fatal warning - shift was already created
        toast.info('Shift was created. Refresh the page if it doesn\'t appear.');
      }
    } catch (error) {
      console.error('[CreateShift] Error:', error);
      console.error('[CreateShift] Error response:', error.response?.data);
      
      // P0 FIX: Extract error message from structured API response
      const errorData = error.response?.data;
      let errorDetail = '';
      let errorCode = '';
      
      // Handle new structured error format: { error_code, message, details }
      if (errorData?.detail?.message) {
        errorDetail = errorData.detail.message;
        errorCode = errorData.detail.error_code || '';
      } else if (typeof errorData?.detail === 'string') {
        errorDetail = errorData.detail;
      } else if (errorData?.message) {
        errorDetail = errorData.message;
      }
      
      // P0: Check if roster is committed - prompt for amendment reason
      // P0 FIX: New shifts don't require amendment - just show any other errors
      // Amendment is only required for editing/deleting existing shifts
      
      // P0: Display specific error messages based on error code
      let errorMessage = errorDetail || error.message || 'Failed to create shift. Please try again.';
      
      // Add context for specific error types
      if (errorCode === 'NOT_FOUND') {
        errorMessage = `${errorDetail}. Please verify the selection and try again.`;
      } else if (errorCode === 'OVERLAPPING_SHIFT') {
        errorMessage = `${errorDetail}. Please choose a different time or officer.`;
      } else if (errorCode === 'VALIDATION_ERROR') {
        errorMessage = `Validation error: ${errorDetail}`;
      }
      
      toast.error(errorMessage);
    }
  };

  // P0: Handle amendment submission
  const handleAmendmentSubmit = async () => {
    if (!pendingShiftData || !formData.amendment_reason?.trim()) {
      toast.error('Please provide an amendment reason (min 5 characters)');
      return;
    }
    
    if (formData.amendment_reason.trim().length < 5) {
      toast.error('Amendment reason must be at least 5 characters');
      return;
    }
    
    try {
      const payload = {
        ...pendingShiftData,
        amendment_reason: formData.amendment_reason.trim(),
      };
      
      console.log('[Amendment] Payload:', JSON.stringify(payload, null, 2));
      
      const response = await api.post('/roster/shifts', payload);
      
      // P0 INTEGRITY: Do NOT show success unless response contains shift.id
      const createdShift = response.data;
      if (!createdShift?.id) {
        console.error('[Amendment] ERROR: Response missing shift.id', createdShift);
        toast.error('Amendment creation failed - invalid server response');
        return;
      }
      
      // P0 INTEGRITY: Immediately insert shift into local roster state
      setRosterData(prevData => {
        if (!prevData?.days) return prevData;
        
        const shiftDate = createdShift.date;
        if (!shiftDate || !prevData.days[shiftDate]) {
          console.log('[Amendment] Shift date not in current week, will refetch');
          return prevData;
        }
        
        const updatedDays = { ...prevData.days };
        const dayData = { ...updatedDays[shiftDate] };
        dayData.shifts = [...(dayData.shifts || []), createdShift];
        dayData.shift_count = (dayData.shift_count || 0) + 1;
        
        if (!createdShift.officer_id) {
          dayData.unfilled_count = (dayData.unfilled_count || 0) + 1;
        } else {
          dayData.filled_count = (dayData.filled_count || 0) + 1;
          if (createdShift.acceptance_status === 'pending_acceptance') {
            dayData.pending_count = (dayData.pending_count || 0) + 1;
          }
        }
        
        const shiftHours = createdShift.net_hours || createdShift.gross_hours || 0;
        dayData.total_hours = (dayData.total_hours || 0) + shiftHours;
        updatedDays[shiftDate] = dayData;
        
        const allShifts = Object.values(updatedDays).flatMap(d => d.shifts || []);
        const newSummary = {
          total_shifts: allShifts.length,
          filled: allShifts.filter(s => s.officer_id).length,
          unfilled: allShifts.filter(s => !s.officer_id).length,
          accepted: allShifts.filter(s => s.acceptance_status === 'accepted').length,
          pending: allShifts.filter(s => s.acceptance_status === 'pending_acceptance').length,
          declined: allShifts.filter(s => s.acceptance_status === 'declined').length,
          unaccepted: allShifts.filter(s => s.acceptance_status === 'unaccepted').length,
        };
        
        console.log('[Amendment] Updated local state with amendment shift:', createdShift.id);
        
        return {
          ...prevData,
          days: updatedDays,
          summary: newSummary,
        };
      });
      
      toast.success('Amendment shift created successfully');
      setShowAmendmentPrompt(false);
      setShowCreateDialog(false);
      setPendingShiftData(null);
      setFormData({
        officer_id: '',
        start_date: '',
        start_time: '06:00',
        end_time: '18:00',
        notes: '',
        break_minutes: 0,
        amendment_reason: '',
      });
      fetchRoster();
    } catch (error) {
      console.error('[Amendment] Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create amendment');
    }
  };

  const handleAcceptShift = async (shiftId) => {
    try {
      setActionLoading(shiftId);
      await api.post(`/roster/shifts/${shiftId}/accept`);
      toast.success('Shift accepted');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept shift');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineShift = async () => {
    if (!selectedShift || !declineReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      setActionLoading(selectedShift.id);
      await api.post(`/roster/shifts/${selectedShift.id}/decline?reason=${encodeURIComponent(declineReason)}`);
      toast.success('Shift declined');
      setShowDeclineDialog(false);
      setDeclineReason('');
      setSelectedShift(null);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline shift');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteShift = async (shiftId, isLocked) => {
    const reason = isLocked ? window.prompt('This shift is locked. Please provide a reason:') : null;
    if (isLocked && !reason) return;
    
    try {
      setActionLoading(shiftId);
      let url = `/roster/shifts/${shiftId}`;
      if (reason) url += `?reason=${encodeURIComponent(reason)}`;
      await api.delete(url);
      toast.success('Shift cancelled');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel shift');
    } finally {
      setActionLoading(null);
    }
  };

  // P0: Bulk Delete all shifts in current week
  const handleBulkDelete = async () => {
    if (!rosterData?.days) return;
    
    // Collect all shift IDs for the current week
    const shiftIds = Object.values(rosterData.days)
      .flatMap(day => day.shifts || [])
      .filter(shift => !shift.is_locked) // Only delete unlocked shifts
      .map(shift => shift.id);
    
    if (shiftIds.length === 0) {
      toast.error('No shifts to delete (all shifts are locked)');
      setShowBulkDeleteDialog(false);
      return;
    }
    
    try {
      setBulkDeleteLoading(true);
      
      const response = await api.delete('/roster/shifts/bulk', {
        data: { shift_ids: shiftIds }
      });
      
      const deleted = response.data?.deleted_count || shiftIds.length;
      toast.success(`Deleted ${deleted} shifts`);
      setShowBulkDeleteDialog(false);
      fetchRoster();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete shifts');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleCommitWeek = async () => {
    if (!window.confirm('Commit this week? Shifts will be locked from editing.')) return;
    
    try {
      setActionLoading('commit-week');
      const weekStr = toLocalDateKey(currentWeek);
      const response = await api.post(`/roster/commit-week?week_start=${weekStr}`);
      toast.success(`Week committed: ${response.data.shifts_locked} shifts locked`);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to commit roster');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCommitDay = async (dateStr) => {
    if (!window.confirm('Commit this day?')) return;
    
    try {
      setActionLoading(`commit-${dateStr}`);
      await api.post(`/roster/commit-day?date=${dateStr}`);
      toast.success('Day committed');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to commit day');
    } finally {
      setActionLoading(null);
    }
  };

  // P1: Edit Shift
  const handleOpenEditDialog = (shift) => {
    setEditingShift(shift);
    // Parse times from shift - they come as ISO strings
    let startTime = '06:00';
    let endTime = '18:00';
    
    try {
      if (shift.start_time) {
        const startDate = new Date(shift.start_time);
        startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
      }
      if (shift.end_time) {
        const endDate = new Date(shift.end_time);
        endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
      }
    } catch (e) {
      console.error('Error parsing shift times:', e);
    }
    
    setEditFormData({
      start_time: startTime,
      end_time: endTime,
      notes: shift.notes || '',
      break_minutes: shift.break_minutes || 0,
    });
    setShowEditDialog(true);
  };

  const handleEditShift = async () => {
    if (!editingShift) return;
    
    try {
      setActionLoading(`edit-${editingShift.id}`);
      
      // Get the date from the shift
      const shiftDate = new Date(editingShift.start_time);
      const dateStr = `${shiftDate.getFullYear()}-${String(shiftDate.getMonth() + 1).padStart(2, '0')}-${String(shiftDate.getDate()).padStart(2, '0')}`;
      
      // Create full datetime strings in Sydney timezone
      const startDateTime = `${dateStr}T${editFormData.start_time}:00+11:00`;
      const endDateTime = `${dateStr}T${editFormData.end_time}:00+11:00`;
      
      await api.put(`/roster/v2/shifts/${editingShift.id}`, {
        start_time: new Date(startDateTime).toISOString(),
        end_time: new Date(endDateTime).toISOString(),
        notes: editFormData.notes || null,
        break_minutes: editFormData.break_minutes || 0,
      });
      
      toast.success('Shift updated');
      setShowEditDialog(false);
      setEditingShift(null);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyWeek = async () => {
    try {
      setActionLoading('copy-week');
      
      // P0-3: Use target date if provided, otherwise default to next week
      let targetWeekDate;
      if (copyTargetDate) {
        targetWeekDate = copyTargetDate;
      } else {
        const nextWeek = new Date(currentWeek);
        nextWeek.setDate(nextWeek.getDate() + 7);
        targetWeekDate = toLocalDateKey(nextWeek);
      }
      
      const sourceWeekDate = toLocalDateKey(currentWeek);
      
      const response = await api.post(
        `/roster/v2/clone-week-from-source?site_id=${siteId}&source_week_start_date=${sourceWeekDate}&target_week_start_date=${targetWeekDate}&mode=${copyKeepOfficers ? 'with_assignments' : 'shifts_only'}&overwrite_target=false`
      );
      
      toast.success(`Roster copied: ${response.data.shifts_created} shifts created`);
      setShowCopyDialog(false);
      setCopyTargetDate('');
      
      // Navigate to target week using proper navigation
      const targetMonday = getMonday(new Date(targetWeekDate));
      const weekStr = toLocalDateKey(targetMonday);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('weekStart', weekStr);
        return params;
      }, { replace: true });
      setCurrentWeekInternal(targetMonday);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy roster');
    } finally {
      setActionLoading(null);
    }
  };

  const openCreateForDay = (day) => {
    // Handle day.date as either a Date object or ISO string from API
    let dateStr;
    if (day.date instanceof Date) {
      dateStr = toLocalDateKey(day.date);
    } else if (typeof day.date === 'string') {
      // It's already a YYYY-MM-DD string or ISO string
      dateStr = day.date.split('T')[0];
    } else {
      // Fallback: use today's local date
      dateStr = toLocalDateKey(new Date());
      console.warn('[openCreateForDay] Unexpected day.date type:', typeof day.date);
    }
    
    setFormData(prev => ({
      ...prev,
      start_date: dateStr,
    }));
    setSelectedDay(day);
    setShowCreateDialog(true);
  };

  // Export functions
  const exportToCSV = () => {
    if (!rosterData) return;
    setExportLoading(true);
    
    try {
      const rows = [['Date', 'Day', 'Officer', 'Start Time', 'End Time', 'Hours', 'Status', 'Notes']];
      const daysList = Object.entries(rosterData.days).sort((a, b) => new Date(a[0]) - new Date(b[0]));
      
      for (const [dateKey, day] of daysList) {
        const dateObj = new Date(day.date);
        const dateStr = dateObj.toLocaleDateString('en-AU');
        const dayName = dateObj.toLocaleDateString('en-AU', { weekday: 'long' });
        
        for (const shift of day.shifts) {
          const hours = calculateHours(shift.start_time, shift.end_time, shift.break_minutes || 0);
          rows.push([
            dateStr,
            dayName,
            shift.officer_name || 'Unassigned',
            formatTimeLocal(shift.start_time),
            formatTimeLocal(shift.end_time),
            formatHoursDecimal(hours),
            shift.acceptance_status?.replace(/_/g, ' ') || '',
            shift.notes || ''
          ]);
        }
      }
      
      const csvContent = rows.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `roster_${siteInfo?.name?.replace(/\s+/g, '_') || 'site'}_${toLocalDateKey(currentWeek)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Roster exported to CSV');
    } catch (error) {
      toast.error('Failed to export roster');
    } finally {
      setExportLoading(false);
    }
  };

  // P1: Drag-to-Duplicate handlers
  const handleDragStart = (e, shift, dateKey) => {
    // Check if ALT key is pressed for duplicate mode
    setIsDragDuplicate(e.altKey);
    setDraggedShift({ ...shift, sourceDateKey: dateKey });
    
    // Set drag data
    e.dataTransfer.effectAllowed = e.altKey ? 'copy' : 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ shiftId: shift.id, sourceDateKey: dateKey }));
    
    // Visual feedback
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedShift(null);
    setIsDragDuplicate(false);
    setDragOverDay(null);
  };

  const handleDragOver = (e, dateKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = isDragDuplicate ? 'copy' : 'move';
    setDragOverDay(dateKey);
  };

  const handleDragLeave = (e) => {
    setDragOverDay(null);
  };

  const handleDrop = async (e, targetDateKey, targetDay) => {
    e.preventDefault();
    setDragOverDay(null);
    
    if (!draggedShift) return;
    
    const sourceDateKey = draggedShift.sourceDateKey;
    
    // Don't do anything if dropping on same day (unless duplicate mode)
    if (sourceDateKey === targetDateKey && !isDragDuplicate) {
      setDraggedShift(null);
      return;
    }
    
    try {
      if (isDragDuplicate) {
        // ALT+Drag: Duplicate shift to target day
        // Extract time from shift
        const startTime = new Date(draggedShift.start_time);
        const endTime = new Date(draggedShift.end_time);
        
        // Get just the time portion in HH:MM format (Sydney time)
        const startTimeStr = formatTimeLocal(draggedShift.start_time);
        const endTimeStr = formatTimeLocal(draggedShift.end_time);
        
        // Create UTC times for target date
        const startTimeUTC = localInputsToUTC(targetDateKey, startTimeStr);
        let endTimeUTC = localInputsToUTC(targetDateKey, endTimeStr);
        
        // Handle overnight shifts
        if (endTimeStr <= startTimeStr) {
          const [year, month, day] = targetDateKey.split('-').map(Number);
          const nextDay = new Date(year, month - 1, day + 1);
          const nextDayStr = toLocalDateKey(nextDay);
          endTimeUTC = localInputsToUTC(nextDayStr, endTimeStr);
        }
        
        const payload = {
          site_id: siteId,
          officer_id: draggedShift.officer_id || null,
          start_time: startTimeUTC,
          end_time: endTimeUTC,
          notes: draggedShift.notes || null,
          require_acceptance: true,
          break_minutes: draggedShift.break_minutes || 0,
        };
        
        await api.post('/roster/shifts', payload);
        toast.success(`Shift duplicated to ${targetDay.date ? formatDateKey(targetDay.date, { weekday: 'short', day: 'numeric', month: 'short' }) : targetDateKey}`);
        fetchRoster();
      } else {
        // Normal drag: Move shift (not implemented yet)
        toast.info('Move shift coming soon. Use ALT+drag to duplicate.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate shift');
    } finally {
      setDraggedShift(null);
      setIsDragDuplicate(false);
    }
  };

  // P0 FIX: CRITICAL - Days grid columns MUST be computed from currentWeek, NOT from rosterData
  // This prevents stale API responses from rendering wrong week's columns
  // We use rosterData only for shift DATA within each computed day
  const days = React.useMemo(() => {
    // P0 FIX: Compute days directly here to ensure currentWeek changes trigger re-render
    const computed = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeek);
      d.setDate(d.getDate() + i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const dayNum = String(d.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${dayNum}`;
      computed.push([dateKey, { date: dateKey, label: d.toLocaleDateString('en-AU', { weekday: 'short' }), shifts: [] }]);
    }
    
    // DIAGNOSTIC LOGGING
    console.log('[Days] Computing days grid:');
    console.log('  - currentWeek:', currentWeek?.toISOString?.());
    console.log('  - computed dateKeys:', computed.map(([k]) => k));
    console.log('  - rosterData:', rosterData ? 'exists' : 'null');
    console.log('  - rosterData.days keys:', rosterData?.days ? Object.keys(rosterData.days) : 'none');
    
    // If we have rosterData, merge shift data into computed days
    if (rosterData?.days) {
      console.log('[Days] MERGING API data into computed days:');
      const merged = computed.map(([dateKey, dayData]) => {
        const apiDayData = rosterData.days[dateKey];
        if (apiDayData) {
          const shiftCount = apiDayData.shifts?.length || 0;
          console.log(`  - [${dateKey}] API has ${shiftCount} shifts, merging...`);
          if (shiftCount > 0) {
            console.log(`    Shift IDs: ${apiDayData.shifts.map(s => s.id).join(', ')}`);
          }
          // CRITICAL: Return merged data with shifts from API
          return [dateKey, { ...dayData, ...apiDayData }];
        } else {
          console.log(`  - [${dateKey}] NO API data for this date`);
        }
        return [dateKey, dayData];
      });
      console.log('[Days] Merge complete. Final day shift counts:');
      merged.forEach(([k, d]) => console.log(`  ${k}: ${d.shifts?.length || 0} shifts`));
      return merged;
    }
    
    return computed;
  }, [currentWeek, rosterData]); // P0 FIX: Added currentWeek as explicit dependency
  
  const summary = rosterData?.summary || {};

  // DIAGNOSTIC: Log what we're about to render
  console.log('[RENDER] days array length:', days.length);
  console.log('[RENDER] days shift counts:', days.map(([k, d]) => `${k}:${d.shifts?.length || 0}`));
  console.log('[RENDER] summary:', summary);

  if (loading && !rosterData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // P0 FIX: Show error state with retry - don't display stale data
  if (rosterError && !rosterData) {
    return (
      <div className="space-y-4">
        {/* Breadcrumb - P1 FIX: Preserve week context in error state */}
        <div className="flex items-center gap-2 text-sm">
          <Link 
            to={`/roster?weekStart=${toLocalDateKey(currentWeek)}`} 
            className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Rosters
          </Link>
        </div>

        {/* Error Card */}
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h2 className="text-lg font-medium text-red-400 mb-2">Failed to Load Roster</h2>
            <p className="text-sm text-zinc-400 mb-4">
              Week: {formatWeekRange(currentWeek)}
            </p>
            <p className="text-xs text-zinc-500 mb-6">
              {rosterError}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={() => fetchRoster()}
                className="bg-red-500 hover:bg-red-600"
                data-testid="retry-roster-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={navigateToToday}
                className="border-zinc-600"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Go to Current Week
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-roster-page">
      {/* Breadcrumb - P1 FIX: Preserve week context when navigating back */}
      <div className="flex items-center gap-2 text-sm">
        <Link 
          to={`/roster?weekStart=${toLocalDateKey(currentWeek)}`} 
          className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Rosters
        </Link>
        <span className="text-zinc-600">/</span>
        <span className="text-zinc-300">{siteInfo?.name || 'Site Roster'}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
              {siteInfo?.name || 'ROSTER'}
            </h1>
            {siteInfo?.site_code && (
              <Badge className="bg-zinc-700 text-zinc-300">{siteInfo.site_code}</Badge>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1">
            {siteInfo?.client_name && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                {siteInfo.client_name}
              </span>
            )}
            {siteInfo?.region && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {siteInfo.region}
              </span>
            )}
            <span>w/c {formatWeekRange(currentWeek)}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Site Quick Switch */}
          <Select value={siteId} onValueChange={handleSiteChange}>
            <SelectTrigger className="w-[200px] bg-[#18181b] border-[#3f3f46] h-9" data-testid="site-switch">
              <MapPin className="w-4 h-4 mr-2 text-zinc-500" />
              <SelectValue placeholder="Switch Site" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#3f3f46] max-h-[300px]">
              {sites.map(site => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Week Navigation - P1 UX: Clearer navigation labels */}
          <div className="flex items-center gap-1">
            <div className="flex items-center bg-[#18181b] border border-[#3f3f46] rounded-sm">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handlePreviousWeek} 
                className="h-9 px-3 text-xs text-zinc-400 hover:text-white" 
                title="Previous Week"
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-3 h-3 mr-0.5" />
                <ChevronLeft className="w-3 h-3 -ml-2" />
                <span className="hidden sm:inline ml-1">Prev</span>
              </Button>
              <span className="text-sm text-zinc-300 px-2 whitespace-nowrap border-x border-[#3f3f46]">
                {currentWeek.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleNextWeek} 
                className="h-9 px-3 text-xs text-zinc-400 hover:text-white" 
                title="Next Week"
                data-testid="next-week-btn"
              >
                <span className="hidden sm:inline mr-1">Next</span>
                <ChevronRight className="w-3 h-3 -mr-2" />
                <ChevronRight className="w-3 h-3 ml-0.5" />
              </Button>
            </div>
            
            {/* "Today" button - always visible */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleToday}
              className="h-9 px-3 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
              data-testid="go-to-today-btn"
              title="Go to current week (Australia/Sydney timezone)"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Today
            </Button>
          </div>

          <Button onClick={fetchRoster} variant="outline" size="sm" className="border-zinc-700 h-9">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          {/* Export */}
          <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="border-zinc-600 h-9" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-sm">
              <DialogHeader>
                <DialogTitle>Export Roster</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Export roster for {siteInfo?.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Button
                  className="w-full justify-start bg-[#27272a] hover:bg-[#3f3f46]"
                  onClick={() => { exportToCSV(); setShowExportDialog(false); }}
                  disabled={exportLoading}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-3 text-emerald-400" />
                  Export as Excel (CSV)
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {isAdmin && (
            <>
              {/* View Mode Toggle */}
              <div className="flex items-center bg-[#18181b] border border-[#3f3f46] rounded-sm">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={`h-9 px-3 ${viewMode === 'standard' ? 'bg-zinc-700' : ''}`}
                  onClick={() => setViewMode('standard')}
                  title="Standard View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={`h-9 px-3 ${viewMode === 'security' ? 'bg-zinc-700' : ''}`}
                  onClick={() => setViewMode('security')}
                  title="Security Format View"
                >
                  <Table className="w-4 h-4" />
                </Button>
              </div>

              {/* P1: Template Buttons */}
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-500/10 h-9"
                onClick={() => setShowSaveAsTemplate(true)}
                data-testid="save-as-template-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Save as Template
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="border-blue-600/50 text-blue-400 hover:bg-blue-500/10 h-9"
                onClick={() => setShowTemplateLibrary(true)}
                data-testid="template-library-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Templates
              </Button>

              {/* Copy Week - P0-3 Enhanced */}
              <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-zinc-600 h-9">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Week
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle>Clone Week Roster</DialogTitle>
                    <DialogDescription className="text-zinc-500">
                      Copy all shifts from current week to a target week
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Source Week</Label>
                      <div className="p-2 bg-zinc-800 rounded text-sm text-zinc-400">
                        {formatWeekRange(currentWeek)}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Target Week Start Date</Label>
                      <Input
                        type="date"
                        value={copyTargetDate}
                        onChange={(e) => setCopyTargetDate(e.target.value)}
                        className="bg-[#09090b] border-[#3f3f46]"
                        placeholder="Select target week..."
                      />
                      <p className="text-xs text-zinc-500">
                        Leave blank to copy to next week
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="keep-officers" 
                        checked={copyKeepOfficers}
                        onCheckedChange={setCopyKeepOfficers}
                      />
                      <Label htmlFor="keep-officers" className="text-zinc-300">
                        Keep officer assignments
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCopyDialog(false)} className="border-zinc-600">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCopyWeek} 
                      className="bg-blue-500 hover:bg-blue-600"
                      disabled={actionLoading === 'copy-week'}
                    >
                      {actionLoading === 'copy-week' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Clone Week
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Commit Week */}
              <Button 
                onClick={handleCommitWeek}
                variant="outline" 
                size="sm" 
                className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10 h-9"
                disabled={rosterData?.is_week_locked || actionLoading === 'commit-week'}
              >
                {actionLoading === 'commit-week' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : rosterData?.is_week_locked ? (
                  <Lock className="w-4 h-4 mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {rosterData?.is_week_locked ? 'Locked' : 'Commit'}
              </Button>

              {/* Bulk Delete Button */}
              <Button 
                onClick={() => setShowBulkDeleteDialog(true)}
                variant="outline" 
                size="sm" 
                className="border-red-600 text-red-400 hover:bg-red-500/10 h-9"
                disabled={rosterData?.is_week_locked}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Week
              </Button>

              {/* Add Shift */}
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600 h-9" data-testid="add-shift-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Shift
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Shift for {siteInfo?.name}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateShift} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Officer</Label>
                      <Select 
                        value={formData.officer_id || "unassigned"} 
                        onValueChange={(v) => setFormData({...formData, officer_id: v === "unassigned" ? "" : v})}
                      >
                        <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {officers.map(officer => (
                            <SelectItem key={officer.id} value={officer.id}>
                              {officer.first_name} {officer.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Date <span className="text-red-400">*</span></Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                          className="bg-[#09090b] border-[#3f3f46]"
                          required
                          data-testid="shift-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Start (24h)</Label>
                        <TimeInput24h
                          value={formData.start_time}
                          onChange={(v) => setFormData({...formData, start_time: v})}
                          placeholder="18:00"
                          className="bg-[#09090b] border-[#3f3f46]"
                          data-testid="shift-start-time"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">End (24h)</Label>
                        <TimeInput24h
                          value={formData.end_time}
                          onChange={(v) => setFormData({...formData, end_time: v})}
                          placeholder="06:00"
                          className="bg-[#09090b] border-[#3f3f46]"
                          data-testid="shift-end-time"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Break (minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="120"
                          value={formData.break_minutes || 0}
                          onChange={(e) => setFormData({...formData, break_minutes: parseInt(e.target.value) || 0})}
                          className="bg-[#09090b] border-[#3f3f46]"
                          placeholder="0"
                          data-testid="shift-break-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Duration</Label>
                        <div className="h-10 px-3 flex items-center bg-[#09090b] border border-[#3f3f46] rounded-md text-zinc-400 font-mono">
                          {(() => {
                            const start = parseTimeInput(formData.start_time);
                            const end = parseTimeInput(formData.end_time);
                            if (start && end) {
                              const [sh, sm] = start.split(':').map(Number);
                              const [eh, em] = end.split(':').map(Number);
                              let diff = (eh * 60 + em) - (sh * 60 + sm);
                              if (diff < 0) diff += 24 * 60;
                              diff -= (formData.break_minutes || 0);
                              const hours = Math.floor(diff / 60);
                              const mins = diff % 60;
                              return `${hours}h ${mins}m`;
                            }
                            return '--';
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Notes</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Optional notes..."
                        className="bg-[#09090b] border-[#3f3f46]"
                      />
                    </div>
                    
                    {/* P0: Patrol Routes Assignment Section */}
                    {availablePatrolRoutes.length > 0 && (
                      <div className="space-y-2 border-t border-zinc-700 pt-4">
                        <Label className="text-zinc-300 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Patrol Routes
                        </Label>
                        <div className="text-xs text-zinc-500 mb-2">
                          Assign patrol routes the guard must complete during this shift
                        </div>
                        
                        {/* Selected Routes */}
                        {formData.patrol_route_ids.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {formData.patrol_route_ids.map(routeId => {
                              const route = availablePatrolRoutes.find(r => r.id === routeId);
                              if (!route) return null;
                              return (
                                <div key={routeId} className="flex items-center justify-between p-2 bg-zinc-800 rounded border border-zinc-700">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">{route.name}</div>
                                    <div className="text-xs text-zinc-500">{route.checkpoint_count} checkpoints</div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1">
                                      <Label className="text-xs text-zinc-400">Runs:</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={formData.patrol_requirements[routeId] || 1}
                                        onChange={(e) => setFormData({
                                          ...formData,
                                          patrol_requirements: {
                                            ...formData.patrol_requirements,
                                            [routeId]: parseInt(e.target.value) || 1
                                          }
                                        })}
                                        className="w-14 h-7 text-sm bg-[#09090b] border-zinc-600"
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setFormData({
                                        ...formData,
                                        patrol_route_ids: formData.patrol_route_ids.filter(id => id !== routeId),
                                        patrol_requirements: Object.fromEntries(
                                          Object.entries(formData.patrol_requirements).filter(([k]) => k !== routeId)
                                        )
                                      })}
                                      className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Add Route Dropdown */}
                        <Select
                          value=""
                          onValueChange={(routeId) => {
                            if (routeId && !formData.patrol_route_ids.includes(routeId)) {
                              setFormData({
                                ...formData,
                                patrol_route_ids: [...formData.patrol_route_ids, routeId],
                                patrol_requirements: {
                                  ...formData.patrol_requirements,
                                  [routeId]: 1
                                }
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="bg-[#09090b] border-zinc-600">
                            <SelectValue placeholder="+ Add Patrol Route" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181b] border-zinc-600">
                            {availablePatrolRoutes
                              .filter(r => !formData.patrol_route_ids.includes(r.id))
                              .map(route => (
                                <SelectItem key={route.id} value={route.id}>
                                  {route.name} ({route.checkpoint_count} checkpoints)
                                </SelectItem>
                              ))
                            }
                            {availablePatrolRoutes.filter(r => !formData.patrol_route_ids.includes(r.id)).length === 0 && (
                              <div className="px-2 py-1 text-xs text-zinc-500">All routes already assigned</div>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-600">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                        Create Shift
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Stats Strip */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-zinc-300">{summary.total_shifts || 0}</div>
            <div className="text-xs text-zinc-500">Shifts</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-emerald-400">{summary.filled || 0}</div>
            <div className="text-xs text-zinc-500">Filled</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-orange-400">{summary.unfilled || 0}</div>
            <div className="text-xs text-zinc-500">Unfilled</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-emerald-400">{summary.accepted || 0}</div>
            <div className="text-xs text-zinc-500">Accepted</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-blue-400">{summary.pending || 0}</div>
            <div className="text-xs text-zinc-500">Pending</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-red-400">{summary.declined || 0}</div>
            <div className="text-xs text-zinc-500">Declined</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-amber-400">{summary.unaccepted || 0}</div>
            <div className="text-xs text-zinc-500">Unaccepted</div>
          </CardContent>
        </Card>
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-mono font-bold text-yellow-400">{rosterData?.total_hours || 0}h</div>
            <div className="text-xs text-zinc-500">Hours</div>
          </CardContent>
        </Card>
      </div>

      {/* Week Lock Banner - P0: Updated to show amendments enabled */}
      {rosterData?.is_week_locked && (
        <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400">
              <strong>Committed</strong> – Amendments enabled
            </span>
          </div>
          <span className="text-xs text-amber-400/70">
            by {rosterData.locked_by_name || 'Admin'} on {formatDateTimeLocal(rosterData.locked_at)}
          </span>
        </div>
      )}

      {/* P0: Amendment Prompt Dialog */}
      <Dialog open={showAmendmentPrompt} onOpenChange={setShowAmendmentPrompt}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Roster is Committed
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This week's roster has been committed. To add a shift, please provide a reason for this amendment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm text-amber-400">
              All amendments are logged for compliance and auditing purposes.
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Amendment Reason <span className="text-red-400">*</span></Label>
              <Textarea
                value={formData.amendment_reason || ''}
                onChange={(e) => setFormData({...formData, amendment_reason: e.target.value})}
                placeholder="e.g., Client requested additional coverage for today's event"
                className="bg-[#09090b] border-[#3f3f46] min-h-[80px]"
                data-testid="amendment-reason-input"
              />
              <p className="text-xs text-zinc-500">Minimum 5 characters required</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAmendmentPrompt(false);
                setPendingShiftData(null);
              }}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAmendmentSubmit}
              className="bg-amber-500 hover:bg-amber-600"
              disabled={!formData.amendment_reason || formData.amendment_reason.trim().length < 5}
            >
              <Check className="w-4 h-4 mr-2" />
              Add Amendment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weekly Grid - Conditional View Mode */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : viewMode === 'security' ? (
        /* P1: Security Format View */
        <RosterSecurityFormatView
          days={days}
          siteId={siteId}
          rosterData={rosterData}
          onCloneDay={fetchRoster}
          onAddShift={(day, dateKey) => openCreateForDay(day)}
          onEditShift={handleOpenEditDialog}
          onDeleteShift={handleDeleteShift}
          onCommitDay={handleCommitDay}
          isAdmin={isAdmin}
          loading={loading}
          api={api}
        />
      ) : (
        /* Standard View */
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {days.map(([dateKey, day]) => (
            <Card key={dateKey} className={`bg-[#18181b] border-[#3f3f46] ${day.is_locked ? 'border-emerald-500/30' : ''}`}>
              <CardHeader className="p-3 border-b border-[#3f3f46]">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-rajdhani text-zinc-300">
                      {formatDateKey(day.date, { weekday: 'short' })}
                    </CardTitle>
                    <span className="text-xs text-zinc-500 font-mono">
                      {formatDateKey(day.date, { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {day.is_locked && <Lock className="w-3 h-3 text-emerald-400" />}
                    <span className="text-xs text-zinc-500">{day.total_hours}h</span>
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  {day.accepted_count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">{day.accepted_count}</span>
                  )}
                  {day.pending_count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">{day.pending_count}</span>
                  )}
                  {day.declined_count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">{day.declined_count}</span>
                  )}
                  {day.unfilled_count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">{day.unfilled_count}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent 
                className={`p-2 min-h-[200px] transition-colors ${dragOverDay === dateKey ? 'bg-blue-500/10 border-2 border-dashed border-blue-500/50' : ''}`}
                onDragOver={(e) => handleDragOver(e, dateKey)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, dateKey, day)}
              >
                <ScrollArea className="h-[250px]">
                  <div className="space-y-2">
                    {/* P0 FIX: Ensure shifts is always an array */}
                    {(!day.shifts || day.shifts.length === 0) ? (
                      <div className="text-center py-8 text-zinc-600 text-xs">
                        No shifts
                        {isAdmin && !day.is_locked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="block mx-auto mt-2 text-blue-400 hover:text-blue-300"
                            onClick={() => openCreateForDay(day)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Shift
                          </Button>
                        )}
                      </div>
                    ) : (
                      (day.shifts || []).map((shift) => {
                        const isMyShift = shift.officer_id === user?.id;
                        const canAcceptDecline = isMyShift && ['pending_acceptance', 'unaccepted'].includes(shift.acceptance_status);
                        const shiftHours = calculateHours(shift.start_time, shift.end_time, shift.break_minutes || 0);
                        
                        return (
                          <div 
                            key={shift.id}
                            className={`p-2 rounded border-l-4 border border-[#3f3f46] cursor-grab active:cursor-grabbing ${SHIFT_CARD_COLORS[shift.acceptance_status] || SHIFT_CARD_COLORS.scheduled} ${draggedShift?.id === shift.id ? 'opacity-50' : ''}`}
                            data-testid={`shift-card-${shift.id}`}
                            draggable={isAdmin}
                            onDragStart={(e) => handleDragStart(e, shift, dateKey)}
                            onDragEnd={handleDragEnd}
                            title={isAdmin ? "Drag to move • ALT+Drag to duplicate" : ""}
                          >
                            <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTimeLocal(shift.start_time)} - {formatTimeLocal(shift.end_time)}
                              </span>
                              <span className="font-mono text-zinc-500">
                                ({formatHoursDecimal(shiftHours)})
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-xs text-zinc-400 mb-2">
                              <User className="w-3 h-3" />
                              {shift.officer_name || <span className="text-orange-400">Unassigned</span>}
                            </div>
                            
                            <Badge className={`${ACCEPTANCE_COLORS[shift.acceptance_status]} text-xs mb-2`}>
                              {shift.acceptance_status?.replace(/_/g, ' ')}
                            </Badge>
                            
                            {shift.decline_reason && (
                              <div className="text-xs text-red-400 italic mb-2 truncate">
                                "{shift.decline_reason}"
                              </div>
                            )}
                            
                            {/* Actions - Large mobile-friendly buttons */}
                            {canAcceptDecline && (
                              <div className="space-y-2 mt-3">
                                <Button
                                  className="w-full h-14 text-base font-bold bg-emerald-500 hover:bg-emerald-600 text-white"
                                  onClick={() => handleAcceptShift(shift.id)}
                                  disabled={actionLoading === shift.id}
                                  data-testid={`accept-shift-${shift.id}`}
                                >
                                  {actionLoading === shift.id ? (
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                  ) : (
                                    <Check className="w-5 h-5 mr-2" />
                                  )}
                                  ACCEPT
                                </Button>
                                <Button
                                  className="w-full h-14 text-base font-bold bg-red-500 hover:bg-red-600 text-white"
                                  onClick={() => {
                                    setSelectedShift(shift);
                                    setShowDeclineDialog(true);
                                  }}
                                  disabled={actionLoading === shift.id}
                                  data-testid={`decline-shift-${shift.id}`}
                                >
                                  <X className="w-5 h-5 mr-2" />
                                  DECLINE
                                </Button>
                              </div>
                            )}
                            
                            {/* Admin delete button */}
                            {isAdmin && !canAcceptDecline && (
                              <div className="flex gap-1 flex-wrap mt-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-zinc-400 hover:text-red-400 text-xs"
                                  onClick={() => handleDeleteShift(shift.id, shift.is_locked)}
                                  disabled={actionLoading === shift.id}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
                
                {isAdmin && !day.is_locked && day.shifts.length > 0 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 h-7 text-xs text-zinc-500 hover:text-zinc-300"
                      onClick={() => openCreateForDay(day)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                    
                    {!rosterData?.is_week_locked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-7 text-xs text-emerald-500 hover:text-emerald-400"
                        onClick={() => handleCommitDay(dateKey)}
                        disabled={actionLoading === `commit-${dateKey}`}
                      >
                        {actionLoading === `commit-${dateKey}` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Lock className="w-3 h-3 mr-1" />
                        )}
                        Commit Day
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Decline Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason for declining..."
              className="bg-[#09090b] border-[#3f3f46] min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeclineDialog(false);
              setDeclineReason('');
              setSelectedShift(null);
            }} className="border-zinc-600">
              Cancel
            </Button>
            <Button 
              onClick={handleDeclineShift} 
              className="bg-red-500 hover:bg-red-600"
              disabled={!declineReason.trim() || actionLoading === selectedShift?.id}
            >
              Decline Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani">Edit Shift Times</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update shift timing for {editingShift?.officer_name || 'this shift'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Start Time (24hr)</Label>
                <TimeInput24
                  value={editFormData.start_time}
                  onChange={(v) => setEditFormData(prev => ({ ...prev, start_time: v }))}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">End Time (24hr)</Label>
                <TimeInput24
                  value={editFormData.end_time}
                  onChange={(v) => setEditFormData(prev => ({ ...prev, end_time: v }))}
                  className="bg-[#09090b] border-[#3f3f46]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Break Minutes</Label>
              <Input
                type="number"
                min="0"
                max="120"
                value={editFormData.break_minutes}
                onChange={(e) => setEditFormData(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))}
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes..."
                className="bg-[#09090b] border-[#3f3f46] min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setEditingShift(null);
            }} className="border-zinc-600">
              Cancel
            </Button>
            <Button 
              onClick={handleEditShift} 
              className="bg-blue-500 hover:bg-blue-600"
              disabled={actionLoading === `edit-${editingShift?.id}`}
            >
              {actionLoading === `edit-${editingShift?.id}` ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Clear Week Shifts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">
                <strong>Warning:</strong> This will permanently delete all unlocked shifts for the week of{' '}
                <span className="font-medium">{formatWeekRange(currentWeek)}</span>.
              </p>
            </div>
            
            <div className="text-zinc-400 text-sm">
              <p>This action will:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Delete all unfilled shifts</li>
                <li>Delete all pending/unaccepted shifts</li>
                <li>Delete shifts that haven't started yet</li>
                <li><span className="text-amber-400">Locked shifts will NOT be deleted</span></li>
              </ul>
            </div>
            
            {rosterData?.summary && (
              <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                <p className="text-zinc-400 text-xs mb-2">Shifts to be deleted:</p>
                <p className="text-white text-lg font-bold">
                  {rosterData.summary.total_shifts || 0} shifts
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowBulkDeleteDialog(false)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete}
              className="bg-red-500 hover:bg-red-600"
              disabled={bulkDeleteLoading}
            >
              {bulkDeleteLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete All Shifts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1: Template Library Modal */}
      <RosterTemplateLibrary
        open={showTemplateLibrary}
        onOpenChange={setShowTemplateLibrary}
        siteId={siteId}
        siteName={siteInfo?.name}
        currentWeek={currentWeek}
        onSelectTemplate={(template, targetWeek) => {
          // Navigate to the generated week
          const targetMonday = new Date(targetWeek);
          const weekStr = toLocalDateKey(targetMonday);
          setSearchParams(params => {
            const newParams = new URLSearchParams(params);
            newParams.set('weekStart', weekStr);
            return newParams;
          }, { replace: true });
          setCurrentWeekInternal(targetMonday);
          fetchRoster(targetMonday);
        }}
      />

      {/* P1: Save as Template Dialog */}
      <SaveAsTemplateDialog
        open={showSaveAsTemplate}
        onOpenChange={setShowSaveAsTemplate}
        siteId={siteId}
        siteName={siteInfo?.name}
        weekStart={toLocalDateKey(currentWeek)}
        shiftCount={rosterData?.summary?.total_shifts || 0}
        totalHours={rosterData?.total_hours || 0}
        onSuccess={() => {
          toast.success('Template saved successfully');
        }}
      />
    </div>
  );
}
